import * as React from "react";

import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ShieldIcon from "@mui/icons-material/Shield";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { getAuth } from "firebase/auth";

import type { ClubInClubNextSessionDto } from "@volley/data";

import userMonitor from "../../userMonitor";
import fetchApi, { logFetchError } from "../../util/fetchApi";
import { navigateTo } from "../../util/navigate";

import {
    useHomeClubInClub,
    type HomeClubInClubValue,
} from "./HomeClubInClubContext";

/**
 * The palette and metrics are lifted from the member portal's own home module
 * (`ClubHomeModule` / `cic.css` in the clubinclub repo) so the two surfaces read as one
 * product — a member tapping through should not notice a seam. Values are inlined rather
 * than themed because they are Club-in-Club's, not Volley's, and should not drift with
 * the rest of the home screen.
 */
const CIC = {
    accent: "#3CE97C",
    accentFg: "#07301a",
    surface: "#243049",
    line: "rgba(255,255,255,0.09)",
    lineStrong: "rgba(255,255,255,0.16)",
    muted: "#94A3B8",
    danger: "#ef4444",
} as const;

/** Full-bleed row below the balance: next session, alerts. Matches `.cic-next-strip`. */
const STRIP = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    color: "#fff",
    padding: "13px 16px",
    background: "rgba(0,0,0,0.18)",
    borderTop: `1px solid ${CIC.line}`,
    textAlign: "left",
} as const;

/** The 34px rounded glyph tile at the head of a strip. Matches `.cic-next-ic`. */
const STRIP_ICON = {
    width: 34,
    height: 34,
    borderRadius: "10px",
    background: "rgba(60,233,124,0.14)",
    color: CIC.accent,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
} as const;

/** Pill button. Matches `.cic-btn.sm`. */
const PILL = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    height: 42,
    padding: "0 18px",
    borderRadius: "30px",
    fontFamily: "'Fira Sans', sans-serif",
    fontSize: "15px",
    fontWeight: 700,
    transition: "filter 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": { filter: "brightness(1.08)" },
    "&.Mui-disabled": { opacity: 0.5 },
} as const;

/**
 * The day portion of a session label, mirroring the portal's `fmtDayLong`: a relative
 * word where one applies, but always with the concrete date, because "Tomorrow" alone
 * stops being unambiguous the moment a member holds more than one booking.
 */
function formatSessionStart(iso: string, timeZone: string): string {
    const start = new Date(iso);
    const dayKey = (d: Date) =>
        new Intl.DateTimeFormat("en-US", {
            timeZone,
            dateStyle: "medium",
        }).format(d);
    const date = new Intl.DateTimeFormat("en-US", {
        timeZone,
        month: "short",
        day: "numeric",
    }).format(start);
    const time = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hour: "numeric",
        minute: "2-digit",
    }).format(start);

    const now = new Date();
    const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    if (dayKey(start) === dayKey(now)) return `Today · ${date} ${time}`;
    if (dayKey(start) === dayKey(tomorrow)) return `Tomorrow · ${date} ${time}`;

    const weekday = new Intl.DateTimeFormat("en-US", {
        timeZone,
        weekday: "short",
    }).format(start);
    return `${weekday}, ${date} ${time}`;
}

/**
 * "Tomorrow · Sep 12 4:00 PM · Court #5".
 *
 * The court matters because the machine moves between them, so this is what tells a
 * member where to go; it is simply absent when CourtReserve could not name it.
 */
export function sessionLabel(
    session: ClubInClubNextSessionDto,
    timeZone: string,
): string {
    const when = formatSessionStart(session.startTime, timeZone);
    return session.courtName ? `${when} · ${session.courtName}` : when;
}

/** A screen in the portal, and any parameter it needs to identify what to show. */
export interface ClubInClubTarget {
    screen: string;
    params?: Record<string, string>;
}

/**
 * Where a tap should send the member: the portal origin the server gave us, carrying a
 * freshly minted Firebase custom token and — when the row points somewhere specific —
 * the screen to open on.
 *
 * Club-in-Club is a separate origin sharing Volley's Firebase directory, so it needs a
 * session of its own. Its entrypoint reads `?token=`, exchanges it via
 * `signInWithCustomToken`, and strips it from the URL — the same handoff the iOS
 * RobotController webview already uses. `?screen=` rides along and is consumed the same
 * way: the portal has no router, so it seeds its navigation stack from this rather than
 * from a path, which is what lets every row here land somewhere useful instead of on the
 * portal's home screen. It validates the name against its own allowlist, so an
 * unrecognised one simply opens home.
 */
export function clubInClubHandoffUrl(
    portalUrl: string,
    token: string,
    target?: ClubInClubTarget,
): string {
    const url = new URL("/", portalUrl);
    url.searchParams.set("token", token);
    if (target) {
        url.searchParams.set("screen", target.screen);
        for (const [key, value] of Object.entries(target.params ?? {})) {
            url.searchParams.set(key, value);
        }
    }
    return url.href;
}

function Strip({
    icon,
    eyebrow,
    title,
    subtitle,
    tone = "normal",
    onClick,
}: {
    icon: React.ReactNode;
    /** Small uppercase label above the title, as the next-session row uses. */
    eyebrow?: string;
    title: string;
    subtitle?: string;
    tone?: "normal" | "danger";
    onClick?: () => void;
}): React.JSX.Element {
    const danger = tone === "danger";
    const body = (
        <>
            <Box
                sx={{
                    ...STRIP_ICON,
                    ...(danger && {
                        background: "rgba(239,68,68,0.2)",
                        color: CIC.danger,
                    }),
                }}
            >
                {icon}
            </Box>
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                {eyebrow && (
                    <Typography
                        sx={{
                            fontSize: "11px",
                            color: CIC.muted,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            fontWeight: 700,
                        }}
                    >
                        {eyebrow}
                    </Typography>
                )}
                <Typography
                    sx={{ color: "#fff", fontWeight: 700, fontSize: "14.5px" }}
                >
                    {title}
                </Typography>
                {subtitle && (
                    <Typography sx={{ color: CIC.muted, fontSize: "12.5px" }}>
                        {subtitle}
                    </Typography>
                )}
            </Stack>
            {onClick && (
                <ChevronRightIcon sx={{ color: CIC.muted, fontSize: 20 }} />
            )}
        </>
    );

    const sx = {
        ...STRIP,
        ...(danger && {
            background: "rgba(239,68,68,0.15)",
            borderTop: `1px solid ${CIC.danger}`,
        }),
    };

    return onClick ? (
        <ButtonBase onClick={onClick} sx={sx}>
            {body}
        </ButtonBase>
    ) : (
        <Box sx={sx}>{body}</Box>
    );
}

/**
 * Whether the widget will render anything at all.
 *
 * Exported because the home screen has to know before it decides who owns the top slot:
 * the widget hides itself for anyone with neither a member record nor a way to create
 * one, and swapping Ask Volley out for something invisible leaves the screen headed by
 * nothing. False while loading — not "no" yet, just not "yes".
 */
export function clubInClubWidgetRenders(value: HomeClubInClubValue): boolean {
    if (value.loading) return false;
    const { member, setup } = value.status;
    return member != null || setup?.state === "ready";
}

/**
 * Club-in-Club on the home screen: point balance, next reserved court session,
 * membership alerts, and a way into the member portal.
 *
 * A persistent section rather than a carousel slide: it carries live numbers and
 * actions, and should not be swipeable past. It renders nothing at all for anyone who
 * has neither a member record nor the ability to create one, which is nearly every
 * Volley user given the club is one pilot location.
 */
export default function ClubInClubWidget(): React.JSX.Element | null {
    const { status, loading } = useHomeClubInClub();
    const [opening, setOpening] = React.useState(false);

    const { member, portalUrl, setup } = status;

    // Leaving for the portal normally tears this page down, so `opening` never needed
    // clearing. It does not in the native app: the portal loads in a drawer over the
    // dashboard, and closing it returns to a page that never unmounted -- with the card
    // still latched disabled until the screen is navigated away from and back.
    //
    // Three signals because no single one covers every way back: `pageshow` fires on a
    // bfcache restore, `visibilitychange` when a backgrounded webview returns, and
    // `focus` when a drawer closes over a page that was never hidden.
    React.useEffect(() => {
        if (!opening) return undefined;
        const reset = () => setOpening(false);
        const onVisible = () => {
            if (document.visibilityState === "visible") reset();
        };
        window.addEventListener("pageshow", reset);
        window.addEventListener("focus", reset);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            window.removeEventListener("pageshow", reset);
            window.removeEventListener("focus", reset);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [opening]);

    const open = React.useCallback(
        async (action: string, target?: ClubInClubTarget) => {
            if (!portalUrl || opening) return;
            setOpening(true);
            userMonitor.addAction("club_in_club_open", { action });
            // Who the token will be minted for. Checked again below: a member who
            // switches account mid-flight must not be handed the previous one's session.
            const signedInAs = getAuth().currentUser;
            try {
                // no-store: a custom token is single-use and short-lived, so a cached
                // response would hand the portal a credential that is already spent.
                const { token } = await fetchApi<{ token: string }>(
                    "/api/users/token",
                    "GET",
                    undefined,
                    { cache: "no-store" },
                );
                if (getAuth().currentUser !== signedInAs) {
                    setOpening(false);
                    return;
                }
                if (typeof token !== "string" || !token.trim()) {
                    throw new Error("Missing handoff token");
                }
                // In place, not a new tab: the portal shows a "← Back to Volley" link in
                // this webview and replaces (not pushes) the cleaned URL, so both it and
                // webview-back return to this screen. A new tab becomes a Safari sheet
                // with its own session and no return path.
                navigateTo(clubInClubHandoffUrl(portalUrl, token, target));
            } catch (err) {
                logFetchError(err, "Could not open the Club-in-Club portal");
                setOpening(false);
            }
        },
        [portalUrl, opening],
    );

    // Withhold while the answer could still change, and stay absent for the large
    // majority who have neither an account nor a way to create one.
    if (loading) return null;
    if (!member && setup?.state !== "ready") return null;

    // The card itself. `overflow: hidden` is what lets the strips run full-bleed to the
    // rounded edge, and the radial wash is the portal's signature.
    const card = {
        margin: "6px 0 18px",
        borderRadius: "18px",
        overflow: "hidden",
        background: `radial-gradient(130% 100% at 100% 0%, rgba(60,233,124,0.14), transparent 60%), ${CIC.surface}`,
        boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
    };

    if (!member) {
        return (
            <Box sx={card}>
                <Strip
                    icon={<SportsTennisIcon sx={{ fontSize: 18 }} />}
                    title="Set up Club in Club"
                    subtitle={
                        setup?.locationName
                            ? `Book the ball machine at ${setup.locationName}`
                            : "Book the ball machine at your club"
                    }
                    onClick={portalUrl ? () => void open("setup") : undefined}
                />
            </Box>
        );
    }

    const next = status.nextSession;
    const suspended = member.status === "suspended";
    const pastDue = member.subscriptionStatus === "past_due";
    const noPlan = member.subscriptionStatus === null;

    return (
        <Box sx={card}>
            {/* Balance. `space-between` leaves the trailing slot free, as the
                    portal's own layout does. */}
            <Box
                sx={{
                    padding: "16px 16px 14px",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: "12px",
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            textTransform: "uppercase",
                            fontSize: "12px",
                            fontWeight: 700,
                            letterSpacing: "0.12em",
                            color: CIC.accent,
                        }}
                    >
                        Point balance
                    </Typography>
                    <Stack
                        direction="row"
                        alignItems="baseline"
                        gap="8px"
                        sx={{ marginTop: "4px" }}
                    >
                        <Typography
                            component="span"
                            sx={{
                                fontFamily: "Gotham, 'Arial Black', sans-serif",
                                fontWeight: 900,
                                fontSize: "40px",
                                lineHeight: 1,
                                color: "#fff",
                            }}
                        >
                            {status.pointBalance ?? 0}
                        </Typography>
                        <Typography
                            component="span"
                            sx={{
                                color: CIC.muted,
                                fontWeight: 700,
                                fontSize: "14px",
                            }}
                        >
                            pts
                        </Typography>
                    </Stack>
                </Box>
            </Box>

            {(suspended || pastDue) && (
                <Strip
                    tone="danger"
                    icon={<ShieldIcon sx={{ fontSize: 18 }} />}
                    title="Membership paused — payment failed"
                    subtitle="Update your card to reactivate and book"
                    onClick={
                        portalUrl
                            ? () =>
                                  void open("billing", {
                                      screen: "account",
                                  })
                            : undefined
                    }
                />
            )}
            {!suspended && !pastDue && noPlan && (
                <Strip
                    icon={<EmojiEventsIcon sx={{ fontSize: 18 }} />}
                    title="Become a member"
                    subtitle="Play all month for less than a few single sessions"
                    onClick={
                        portalUrl
                            ? () => void open("enroll", { screen: "enroll" })
                            : undefined
                    }
                />
            )}

            {/* The portal owns booking, top-ups and account management, so these are
                    entry points into it rather than flows of their own. Absent entirely
                    until tap-through is switched on server-side, because a button that
                    cannot do anything is worse than no button. */}
            {portalUrl && (
                <Box
                    sx={{
                        display: "flex",
                        gap: "10px",
                        padding: "0 14px 14px",
                    }}
                >
                    <ButtonBase
                        disabled={opening}
                        onClick={() => void open("book", { screen: "book" })}
                        sx={{
                            ...PILL,
                            flex: 1,
                            background: CIC.accent,
                            color: CIC.accentFg,
                        }}
                    >
                        <CalendarMonthIcon sx={{ fontSize: 17 }} />
                        Book a session
                    </ButtonBase>
                    <ButtonBase
                        disabled={opening}
                        onClick={() =>
                            void open("buy_points", { screen: "reload" })
                        }
                        sx={{
                            ...PILL,
                            flex: "0 0 auto",
                            background: "transparent",
                            color: "#fff",
                            boxShadow: `inset 0 0 0 1.5px ${CIC.lineStrong}`,
                            "&:hover": {
                                background: "rgba(255,255,255,0.05)",
                            },
                        }}
                    >
                        <AddIcon sx={{ fontSize: 17 }} />
                        Buy points
                    </ButtonBase>
                </Box>
            )}

            {next ? (
                <>
                    <Strip
                        icon={<CalendarMonthIcon sx={{ fontSize: 18 }} />}
                        eyebrow="Next session"
                        title={sessionLabel(next, member.timezone)}
                        onClick={
                            portalUrl
                                ? () =>
                                      void open("reservation", {
                                          screen: "reservation",
                                          params: {
                                              id: next.seatBookingId,
                                          },
                                      })
                                : undefined
                        }
                    />
                    {status.upcomingSessionCount > 1 && (
                        <ButtonBase
                            onClick={
                                portalUrl
                                    ? () =>
                                          void open("reservations", {
                                              screen: "reservations",
                                          })
                                    : undefined
                            }
                            disabled={!portalUrl}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "6px",
                                width: "100%",
                                padding: "11px 16px",
                                background: "rgba(0,0,0,0.10)",
                                borderTop: `1px solid ${CIC.line}`,
                                color: CIC.accent,
                                fontFamily: "'Fira Sans', sans-serif",
                                fontWeight: 700,
                                fontSize: "13px",
                            }}
                        >
                            {`View all ${status.upcomingSessionCount} upcoming sessions`}
                            <ArrowForwardIcon sx={{ fontSize: 15 }} />
                        </ButtonBase>
                    )}
                </>
            ) : (
                <Typography
                    sx={{
                        color: CIC.muted,
                        fontSize: "13px",
                        lineHeight: 1.5,
                        padding: "12px 18px 16px",
                    }}
                >
                    No upcoming sessions yet.
                </Typography>
            )}
        </Box>
    );
}
